// libs
import React, { useState, memo, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

// styles
import { Title, EcomPolicyCardWrapper, Container } from './styles';

// components
import ContentWrapper from './components/ContentWrapper';
import PincodeServiceExp from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeServiceNew/PincodeServiceExp';
import PincodeService from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeServiceNew';
import MultiSellerInfo from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeServiceNewV2/MultiSellerInfo';
import EcomPolicyCard from './components/EcomPolicyCard';

// analytics
import { trackEcomPolicy } from './analytics';

// types
import { PincodeApiResponse } from 'shared/layouts/DetailsPage/components/ProductInfo/types';

// api
import { checkPinCodeService } from 'shared/api/pin-code-service';

// helpers
import { getPincodeInfo } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';
import {
  getPinCodeToStorage,
  getIsServicable,
} from 'shared/layouts/DetailsPage/components/ProductInfo/Mobile/components/PinCodeOverlaySheet/helpers';

//hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useDomainConfig } from 'shared/domainConfig/context/context';

// constants
const TITLE = 'Select Delivery Location';
const POLICY = ['cash_on_delivery', 'return_and_exchanges', 'delivery_details'];

interface IEcomPolicyItem {
  image: string;
  text: string;
  enable?: boolean;
}

interface IEcomPolicyProps {
  cashOnDelivery: IEcomPolicyItem;
  returnAndExchange: IEcomPolicyItem;
  deliveryDetails: IEcomPolicyItem;
  productId: number;
  fetchDeliveryorShippedDate: (
    pinode: string,
    productSKU: string[],
    gatewayApiEnabled: boolean
  ) => void;
  inStockSKUs: string[];
  deliveryOrShippingText: string;
  isFetching: boolean;
  isEddEnabled: boolean;
}

function EcomPolicy({
  deliveryDetails,
  returnAndExchange,
  cashOnDelivery,
  productId,
  isFetching,
  inStockSKUs,
  deliveryOrShippingText,
  fetchDeliveryorShippedDate,
  isEddEnabled,
}: IEcomPolicyProps) {
  const [showOverlaySheet, setShowOverlaySheet] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [isServicable, setIsServicable] = useState(true);
  const [apiResponse] = useState(getPinCodeToStorage());
  const [callServiceabilityAPI, setCallServiceabilityAPI] = useState(true);
  const [serviceabiltyResponse, setServiceabiltyResponse] = useState<PincodeApiResponse>({});
  const gatewayApiEnabled = useRemoteConfig('gatewayApiEnabled');
  
  const {
    ECOM_STRIP_ALT: { FREE_DELIVERY, COD_AVAILABLE, RETURN_AND_EXCHANGE }
  } = useDomainConfig();
  const onOverlayHide = () => {
    setShowOverlaySheet(false);
  };

  const handleClick = (enable: boolean, item: string) => {
    if (!enable) {
      return;
    }
    trackEcomPolicy(item);
    setShowOverlaySheet(true);
    setSelectedItem(item);
  };

  // deliveryOrShippingText is returned from serviceability api, so if we disable edd flag then deliveryOrShippingText
  // would be undefined and if serviceability api gives null data, for both cases will display delivery text using
  // deliveryDetails.text
  const policies = [
    { ...cashOnDelivery, alt: COD_AVAILABLE },
    { ...returnAndExchange, alt: RETURN_AND_EXCHANGE },
    {
      ...deliveryDetails,
      alt: FREE_DELIVERY,
      ...(deliveryOrShippingText && { text: deliveryOrShippingText }),
    },
  ];

  useEffect(() => {
    // edd feature flag experiment
    if (isEddEnabled) return;

    if (apiResponse) {
      setIsServicable(getIsServicable(apiResponse));
    }
  }, [apiResponse, isEddEnabled]);

  useEffect(() => {
    const controller = new AbortController();
    if (isFetching || isEmpty(productId)) {
      return;
    }

    // we only want to call serviceability api for the page load
    if (!isEddEnabled || callServiceabilityAPI === false) return;

    const { pincode, pincodeResponseAvailable } = getPincodeInfo();

    if (pincode !== '') {
      if (pincodeResponseAvailable) {
        fetchDeliveryorShippedDate(pincode, inStockSKUs, gatewayApiEnabled);
        setCallServiceabilityAPI(false);
        return;
      }

      checkPinCodeService(Number(pincode)).then((res: PincodeApiResponse) => {
        if (controller.signal.aborted) return;
        if (res.response.isServicable === 1) {
          fetchDeliveryorShippedDate(pincode, inStockSKUs, gatewayApiEnabled);
          setCallServiceabilityAPI(false);
          setServiceabiltyResponse({ ...res, pincode });
          setIsServicable(true);
          return;
        }
        setIsServicable(false);
      });

      setCallServiceabilityAPI(false);
    } else {
      fetchDeliveryorShippedDate(pincode, inStockSKUs, gatewayApiEnabled);
      setCallServiceabilityAPI(false);
    }
    return () => {
      controller.abort();
    };
  }, [
    callServiceabilityAPI,
    fetchDeliveryorShippedDate,
    isFetching,
    productId,
    inStockSKUs,
    isEddEnabled,
    gatewayApiEnabled,
  ]);

  return (
    <Container data-test="dweb-ecom-policy">
      <Title>{TITLE}</Title>
      {isEddEnabled ? (
        <PincodeServiceExp
          isMobile
          setIsServiceable={setIsServicable}
          inStockSKUs={inStockSKUs}
          fetchDeliveryorShippedDate={fetchDeliveryorShippedDate}
          serviceabiltyResponse={serviceabiltyResponse}
        />
      ) : (
        <PincodeService isMobile setIsServiceable={setIsServicable} />
      )}
      <MultiSellerInfo />
      {isServicable && (
        <EcomPolicyCardWrapper data-at="ecom-strip">
          {policies.map((policy, index) => {
            if (!policy) {
              return null;
            }
            const name = POLICY[index];

            return (
              <EcomPolicyCard
                key={name}
                image={policy.image}
                text={policy.text}
                alt={policy.alt}
                enable={policy.enable}
                policyName={name}
                onClick={handleClick}
              />
            );
          })}

          {showOverlaySheet && (
            <ContentWrapper
              productId={productId}
              selectedItem={selectedItem}
              showOverlaySheet={showOverlaySheet}
              onOverlayHide={onOverlayHide}
              key={selectedItem}
            />
          )}
        </EcomPolicyCardWrapper>
      )}
    </Container>
  );
}

export default memo(EcomPolicy);
